import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { contractApi, propertyApi, tenantApi } from '../../services/api';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const ContractForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [formData, setFormData] = useState({
    // Selection
    propertyId: '',
    tenantId: '',

    // I. Parties
    landlordName: '',
    landlordAddress: '',
    landlordType: 'personne physique',
    landlordEmail: '',
    mandataireName: '',
    mandataireAddress: '',

    // II. Property details
    propertyAddress: '',
    propertyFiscalId: '',
    propertyHabitatType: 'immeuble collectif',
    propertyLegalRegime: 'copropriété',
    propertyConstructionPeriod: '',
    propertySurface: '',
    propertyRoomsCount: '',
    propertyOtherParts: '',
    propertyEquipments: '',
    propertyHeatingMode: 'individuel',
    propertyWaterHeatingMode: 'individuelle',
    propertyDpeClass: '',
    propertyDestination: "usage d'habitation",
    propertyPrivateAccessories: '',
    propertyCommonAreas: '',
    propertyTechEquipment: '',

    // III. Contract duration
    contractStartDate: '',
    contractDuration: 3,
    contractDurationType: 'standard',
    contractReducedReason: '',

    // IV. Financial conditions
    monthlyRent: '',
    rentSubjectToEvolutionCap: false,
    rentSubjectToReferenceCap: false,
    rentReference: '',
    rentReferenceMajorated: '',
    rentComplement: '',
    rentComplementJustification: '',
    previousTenantRent: '',
    previousTenantRentDate: '',
    previousTenantLastRevision: '',
    rentRevisionDate: '',
    rentIrlReference: '',
    chargesPaymentMode: 'provisions',
    chargesAmount: '',
    chargesForfaitRevision: '',
    paymentFrequency: 'mensuel',
    paymentTiming: 'à échoir',
    paymentDate: '1er du mois',
    paymentLocation: '',
    annualEnergyExpenses: '',
    annualEnergyExpensesYear: new Date().getFullYear(),

    // V. Works
    worksImprovementAmount: '',
    worksImprovementNature: '',

    // VI. Guarantees
    securityDeposit: '',

    // VII & VIII. Clauses
    solidarityClause: false,
    resolutoryClause: true,

    // IX. Fees
    feesVisitAndFileMaxPerM2: '',
    feesInventoryMaxPerM2: '',
    feesTenantVisitAndFile: '',
    feesTenantInventory: '',

    // X. Other conditions
    otherConditions: '',

    // Annexes
    annexDpe: true,
    annexLeadRisk: false,
    annexAsbestos: true,
    annexElectricGas: true,
    annexNaturalRisks: false,
    annexNotice: true,
    annexInventory: true,

    // Signature
    signaturePlace: ''
  });

  const steps = [
    { title: 'Sélection', description: 'Propriété et locataire' },
    { title: 'Parties', description: 'Bailleur et locataire' },
    { title: 'Logement', description: 'Caractéristiques du bien' },
    { title: 'Durée', description: 'Date et durée du contrat' },
    { title: 'Conditions financières', description: 'Loyer et charges' },
    { title: 'Garanties & Clauses', description: 'Dépôt et conditions' },
    { title: 'Annexes', description: 'Documents joints' }
  ];

  useEffect(() => {
    fetchProperties();
    if (id) {
      fetchContract();
    }
  }, [id]);

  const fetchProperties = async () => {
    try {
      const response = await propertyApi.getAll();
      // Filter only French properties
      const frenchProperties = response.data.filter(
        p => p.country.toLowerCase() === 'france' || p.country.toLowerCase() === 'fr'
      );
      setProperties(frenchProperties);
    } catch (error) {
      toast.error('Échec du chargement des propriétés');
      console.error(error);
    }
  };

  const fetchTenantsByProperty = async (propertyId) => {
    try {
      const response = await tenantApi.getByProperty(propertyId);
      setTenants(response.data);
    } catch (error) {
      toast.error('Échec du chargement des locataires');
      console.error(error);
    }
  };

  const fetchContract = async () => {
    try {
      const response = await contractApi.getById(id);
      setFormData(response.data);
      if (response.data.propertyId) {
        await fetchTenantsByProperty(response.data.propertyId);
      }
    } catch (error) {
      toast.error('Échec du chargement du contrat');
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePropertyChange = async (e) => {
    const propertyId = e.target.value;
    setFormData(prev => ({ ...prev, propertyId, tenantId: '' }));

    if (propertyId) {
      await fetchTenantsByProperty(propertyId);
      const property = properties.find(p => p.id === parseInt(propertyId));
      setSelectedProperty(property);

      // Pre-fill property information
      setFormData(prev => ({
        ...prev,
        propertyAddress: `${property.address}, ${property.city} ${property.postalCode}`,
      }));
    } else {
      setTenants([]);
      setSelectedProperty(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert string values to proper types
      const contractData = {
        ...formData,
        propertyId: parseInt(formData.propertyId),
        tenantId: parseInt(formData.tenantId),
        propertySurface: parseFloat(formData.propertySurface) || 0,
        propertyRoomsCount: parseInt(formData.propertyRoomsCount) || 0,
        contractDuration: parseInt(formData.contractDuration) || 3,
        monthlyRent: parseFloat(formData.monthlyRent) || 0,
        rentReference: parseFloat(formData.rentReference) || 0,
        rentReferenceMajorated: parseFloat(formData.rentReferenceMajorated) || 0,
        rentComplement: parseFloat(formData.rentComplement) || 0,
        previousTenantRent: parseFloat(formData.previousTenantRent) || 0,
        chargesAmount: parseFloat(formData.chargesAmount) || 0,
        energySavingsAmount: parseFloat(formData.energySavingsAmount) || 0,
        energySavingsDuration: parseInt(formData.energySavingsDuration) || 0,
        colocationInsuranceAmount: parseFloat(formData.colocationInsuranceAmount) || 0,
        colocationInsuranceMonthly: parseFloat(formData.colocationInsuranceMonthly) || 0,
        firstPaymentTotal: parseFloat(formData.firstPaymentTotal) || 0,
        rentReevaluationAmount: parseFloat(formData.rentReevaluationAmount) || 0,
        annualEnergyExpensesYear: parseInt(formData.annualEnergyExpensesYear) || new Date().getFullYear(),
        worksImprovementAmount: parseFloat(formData.worksImprovementAmount) || 0,
        worksRentIncrease: parseFloat(formData.worksRentIncrease) || 0,
        worksRentDecrease: parseFloat(formData.worksRentDecrease) || 0,
        securityDeposit: parseFloat(formData.securityDeposit) || 0,
        feesVisitAndFileMaxPerM2: parseFloat(formData.feesVisitAndFileMaxPerM2) || 0,
        feesInventoryMaxPerM2: parseFloat(formData.feesInventoryMaxPerM2) || 0,
        feesTenantVisitAndFile: parseFloat(formData.feesTenantVisitAndFile) || 0,
        feesTenantInventory: parseFloat(formData.feesTenantInventory) || 0,
      };

      if (id) {
        await contractApi.update(id, contractData);
        toast.success('Contrat mis à jour avec succès');
      } else {
        await contractApi.create(contractData);
        toast.success('Contrat créé avec succès');
      }
      navigate('/contracts');
    } catch (error) {
      toast.error(id ? 'Échec de la mise à jour du contrat' : 'Échec de la création du contrat');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderSelectionStep();
      case 1:
        return renderPartiesStep();
      case 2:
        return renderPropertyStep();
      case 3:
        return renderDurationStep();
      case 4:
        return renderFinancialStep();
      case 5:
        return renderGuaranteesStep();
      case 6:
        return renderAnnexesStep();
      default:
        return null;
    }
  };

  const renderSelectionStep = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Propriété *
        </label>
        <select
          name="propertyId"
          value={formData.propertyId}
          onChange={handlePropertyChange}
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">Sélectionner une propriété</option>
          {properties.map(property => (
            <option key={property.id} value={property.id}>
              {property.name} - {property.address}, {property.city}
            </option>
          ))}
        </select>
        {properties.length === 0 && (
          <p className="mt-1 text-sm text-red-600">
            Aucune propriété en France trouvée. Veuillez d'abord ajouter une propriété située en France.
          </p>
        )}
      </div>

      {formData.propertyId && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Locataire *
          </label>
          <select
            name="tenantId"
            value={formData.tenantId}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Sélectionner un locataire</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.firstName} {tenant.lastName}
              </option>
            ))}
          </select>
          {tenants.length === 0 && (
            <p className="mt-1 text-sm text-red-600">
              Aucun locataire trouvé pour cette propriété. Veuillez d'abord ajouter un locataire.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderPartiesStep = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Bailleur (Landlord)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nom complet *
            </label>
            <input
              type="text"
              name="landlordName"
              value={formData.landlordName}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type de bailleur *
            </label>
            <select
              name="landlordType"
              value={formData.landlordType}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="personne physique">Personne physique</option>
              <option value="personne morale">Personne morale</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Adresse *
            </label>
            <textarea
              name="landlordAddress"
              value={formData.landlordAddress}
              onChange={handleChange}
              required
              rows="2"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="landlordEmail"
              value={formData.landlordEmail}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Mandataire (Agent) - Optionnel</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nom du mandataire
            </label>
            <input
              type="text"
              name="mandataireName"
              value={formData.mandataireName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Adresse du mandataire
            </label>
            <input
              type="text"
              name="mandataireAddress"
              value={formData.mandataireAddress}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPropertyStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Adresse complète du logement *
          </label>
          <textarea
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={handleChange}
            required
            rows="2"
            placeholder="Adresse, bâtiment, étage, porte"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Identifiant fiscal
          </label>
          <input
            type="text"
            name="propertyFiscalId"
            value={formData.propertyFiscalId}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Type d'habitat *
          </label>
          <select
            name="propertyHabitatType"
            value={formData.propertyHabitatType}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="immeuble collectif">Immeuble collectif</option>
            <option value="individuel">Individuel</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Régime juridique *
          </label>
          <select
            name="propertyLegalRegime"
            value={formData.propertyLegalRegime}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="mono propriété">Mono propriété</option>
            <option value="copropriété">Copropriété</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Période de construction *
          </label>
          <select
            name="propertyConstructionPeriod"
            value={formData.propertyConstructionPeriod}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Sélectionner...</option>
            <option value="avant 1949">Avant 1949</option>
            <option value="de 1949 à 1974">De 1949 à 1974</option>
            <option value="de 1975 à 1989">De 1975 à 1989</option>
            <option value="de 1989 à 2005">De 1989 à 2005</option>
            <option value="depuis 2005">Depuis 2005</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Surface habitable (m²) *
          </label>
          <input
            type="number"
            step="0.01"
            name="propertySurface"
            value={formData.propertySurface}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Nombre de pièces principales *
          </label>
          <input
            type="number"
            name="propertyRoomsCount"
            value={formData.propertyRoomsCount}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Classe DPE *
          </label>
          <select
            name="propertyDpeClass"
            value={formData.propertyDpeClass}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Sélectionner...</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="E">E</option>
            <option value="F">F</option>
            <option value="G">G</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Mode de chauffage *
          </label>
          <select
            name="propertyHeatingMode"
            value={formData.propertyHeatingMode}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="individuel">Individuel</option>
            <option value="collectif">Collectif</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Mode d'eau chaude sanitaire *
          </label>
          <select
            name="propertyWaterHeatingMode"
            value={formData.propertyWaterHeatingMode}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="individuelle">Individuelle</option>
            <option value="collective">Collective</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Autres parties (grenier, terrasse, balcon, jardin, etc.)
          </label>
          <input
            type="text"
            name="propertyOtherParts"
            value={formData.propertyOtherParts}
            onChange={handleChange}
            placeholder="Ex: Balcon 5m², Cave"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Équipements (cuisine équipée, installations sanitaires, etc.)
          </label>
          <input
            type="text"
            name="propertyEquipments"
            value={formData.propertyEquipments}
            onChange={handleChange}
            placeholder="Ex: Cuisine équipée, Salle de bain avec baignoire"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Locaux privatifs accessoires (cave, parking, garage)
          </label>
          <input
            type="text"
            name="propertyPrivateAccessories"
            value={formData.propertyPrivateAccessories}
            onChange={handleChange}
            placeholder="Ex: Parking n°12, Cave"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Parties communes (ascenseur, espaces verts, etc.)
          </label>
          <input
            type="text"
            name="propertyCommonAreas"
            value={formData.propertyCommonAreas}
            onChange={handleChange}
            placeholder="Ex: Ascenseur, Local à vélos"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
      </div>
    </div>
  );

  const renderDurationStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Date de prise d'effet *
          </label>
          <input
            type="date"
            name="contractStartDate"
            value={formData.contractStartDate}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Durée du contrat (années) *
          </label>
          <select
            name="contractDuration"
            value={formData.contractDuration}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="1">1 an (durée réduite)</option>
            <option value="3">3 ans (personne physique)</option>
            <option value="6">6 ans (personne morale)</option>
          </select>
        </div>

        {formData.contractDuration === '1' && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Raison de la durée réduite *
            </label>
            <textarea
              name="contractReducedReason"
              value={formData.contractReducedReason}
              onChange={handleChange}
              required
              rows="2"
              placeholder="Événement précis justifiant la durée réduite (raisons professionnelles ou familiales)"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderFinancialStep = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Loyer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Loyer mensuel (€) *
            </label>
            <input
              type="number"
              step="0.01"
              name="monthlyRent"
              value={formData.monthlyRent}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Dépôt de garantie (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="securityDeposit"
              value={formData.securityDeposit}
              onChange={handleChange}
              placeholder="Maximum 1 mois de loyer hors charges"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date de révision du loyer
            </label>
            <input
              type="text"
              name="rentRevisionDate"
              value={formData.rentRevisionDate}
              onChange={handleChange}
              placeholder="Ex: 1er janvier"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trimestre de référence IRL
            </label>
            <input
              type="text"
              name="rentIrlReference"
              value={formData.rentIrlReference}
              onChange={handleChange}
              placeholder="Ex: 3ème trimestre 2024"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Zones tendues (si applicable)</h4>
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="rentSubjectToEvolutionCap"
                checked={formData.rentSubjectToEvolutionCap}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Soumis au plafond d'évolution</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="rentSubjectToReferenceCap"
                checked={formData.rentSubjectToReferenceCap}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Soumis au loyer de référence majoré</span>
            </label>
          </div>

          {formData.rentSubjectToReferenceCap && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Loyer de référence (€/m²)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="rentReference"
                  value={formData.rentReference}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Loyer de référence majoré (€/m²)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="rentReferenceMajorated"
                  value={formData.rentReferenceMajorated}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Complément de loyer (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="rentComplement"
                  value={formData.rentComplement}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Justification du complément
                </label>
                <input
                  type="text"
                  name="rentComplementJustification"
                  value={formData.rentComplementJustification}
                  onChange={handleChange}
                  placeholder="Ex: Caractéristiques exceptionnelles du logement"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Locataire précédent (si applicable)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Loyer précédent (€)
              </label>
              <input
                type="number"
                step="0.01"
                name="previousTenantRent"
                value={formData.previousTenantRent}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date du loyer
              </label>
              <input
                type="date"
                name="previousTenantRentDate"
                value={formData.previousTenantRentDate}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dernière révision
              </label>
              <input
                type="date"
                name="previousTenantLastRevision"
                value={formData.previousTenantLastRevision}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Charges</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Mode de règlement des charges *
            </label>
            <select
              name="chargesPaymentMode"
              value={formData.chargesPaymentMode}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="provisions">Provisions avec régularisation annuelle</option>
              <option value="periodic">Paiement périodique sans provision</option>
              <option value="forfait">Forfait (colocation)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant mensuel des charges (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="chargesAmount"
              value={formData.chargesAmount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {formData.chargesPaymentMode === 'forfait' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Modalités de révision du forfait (colocation)
              </label>
              <input
                type="text"
                name="chargesForfaitRevision"
                value={formData.chargesForfaitRevision}
                onChange={handleChange}
                placeholder="Ex: Révision annuelle selon l'IRL"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Économies d'énergie (si applicable)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant participation (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="energySavingsAmount"
              value={formData.energySavingsAmount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Durée (mois)
            </label>
            <input
              type="number"
              name="energySavingsDuration"
              value={formData.energySavingsDuration}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Justification
            </label>
            <input
              type="text"
              name="energySavingsJustification"
              value={formData.energySavingsJustification}
              onChange={handleChange}
              placeholder="Ex: Travaux d'isolation"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Colocation - Assurance (si applicable)</h3>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="colocationInsurance"
              checked={formData.colocationInsurance}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label className="ml-2 text-sm text-gray-700">
              Assurance souscrite par le bailleur pour le compte des colocataires
            </label>
          </div>

          {formData.colocationInsurance && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Montant annuel (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="colocationInsuranceAmount"
                  value={formData.colocationInsuranceAmount}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Montant mensuel (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="colocationInsuranceMonthly"
                  value={formData.colocationInsuranceMonthly}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Modalités de paiement</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Périodicité *
            </label>
            <select
              name="paymentFrequency"
              value={formData.paymentFrequency}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="mensuel">Mensuel</option>
              <option value="trimestriel">Trimestriel</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Échéance *
            </label>
            <select
              name="paymentTiming"
              value={formData.paymentTiming}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="à échoir">À échoir (payé d'avance)</option>
              <option value="à terme échu">À terme échu (payé après)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date de paiement *
            </label>
            <input
              type="text"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleChange}
              required
              placeholder="Ex: 1er du mois"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lieu de paiement
            </label>
            <input
              type="text"
              name="paymentLocation"
              value={formData.paymentLocation}
              onChange={handleChange}
              placeholder="Ex: Virement bancaire"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant total 1ère échéance (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="firstPaymentTotal"
              value={formData.firstPaymentTotal}
              onChange={handleChange}
              placeholder="Loyer + charges + autres"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Réévaluation du loyer (renouvellement uniquement)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant de la hausse/baisse (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="rentReevaluationAmount"
              value={formData.rentReevaluationAmount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Modalité d'application
            </label>
            <select
              name="rentReevaluationMode"
              value={formData.rentReevaluationMode}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Sélectionner</option>
              <option value="par tiers">Par tiers (sur 3 ans)</option>
              <option value="par sixième">Par sixième (sur 6 ans)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Dépenses énergétiques (information)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant ou fourchette estimé(e) annuel(le)
            </label>
            <input
              type="text"
              name="annualEnergyExpenses"
              value={formData.annualEnergyExpenses}
              onChange={handleChange}
              placeholder="Ex: 800-1200 €"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Année de référence
            </label>
            <input
              type="number"
              name="annualEnergyExpensesYear"
              value={formData.annualEnergyExpensesYear}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuaranteesStep = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Travaux</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Montant des travaux d'amélioration (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="worksImprovementAmount"
              value={formData.worksImprovementAmount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nature des travaux
            </label>
            <input
              type="text"
              name="worksImprovementNature"
              value={formData.worksImprovementNature}
              onChange={handleChange}
              placeholder="Ex: Rénovation de la cuisine"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Diminution de loyer - Travaux locataire (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="worksRentDecrease"
              value={formData.worksRentDecrease}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Détails diminution de loyer
            </label>
            <input
              type="text"
              name="worksRentDecreaseDetails"
              value={formData.worksRentDecreaseDetails}
              onChange={handleChange}
              placeholder="Durée et modalités de dédommagement"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Clauses</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                name="solidarityClause"
                checked={formData.solidarityClause}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </div>
            <div className="ml-3">
              <label className="font-medium text-gray-700">
                Clause de solidarité
              </label>
              <p className="text-sm text-gray-500">
                En cas de pluralité de locataires, les locataires sont solidaires et indivisibles dans leurs obligations
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                name="resolutoryClause"
                checked={formData.resolutoryClause}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </div>
            <div className="ml-3">
              <label className="font-medium text-gray-700">
                Clause résolutoire
              </label>
              <p className="text-sm text-gray-500">
                Résiliation de plein droit en cas de non-paiement, non-souscription d'assurance, ou trouble de voisinage
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Honoraires (si applicable)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Plafond visite/dossier/bail (€/m²)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesVisitAndFileMaxPerM2"
              value={formData.feesVisitAndFileMaxPerM2}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Plafond état des lieux (€/m²)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesInventoryMaxPerM2"
              value={formData.feesInventoryMaxPerM2}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Honoraires locataire - visite/dossier/bail (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesTenantVisitAndFile"
              value={formData.feesTenantVisitAndFile}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Honoraires locataire - état des lieux (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesTenantInventory"
              value={formData.feesTenantInventory}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Honoraires bailleur - visite/dossier/bail (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesLandlordVisitAndFile"
              value={formData.feesLandlordVisitAndFile}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Honoraires bailleur - état des lieux (€)
            </label>
            <input
              type="number"
              step="0.01"
              name="feesLandlordInventory"
              value={formData.feesLandlordInventory}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Autres prestations bailleur
            </label>
            <input
              type="text"
              name="feesLandlordOther"
              value={formData.feesLandlordOther}
              onChange={handleChange}
              placeholder="Détail des autres prestations et conditions de rémunération"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Autres conditions particulières
        </label>
        <textarea
          name="otherConditions"
          value={formData.otherConditions}
          onChange={handleChange}
          rows="4"
          placeholder="Conditions supplémentaires à ajouter au contrat"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>
    </div>
  );

  const renderAnnexesStep = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Cochez les documents qui seront annexés au contrat. Certains documents sont obligatoires selon la loi.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexDpe"
              checked={formData.annexDpe}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              Diagnostic de performance énergétique (DPE) *
            </label>
            <p className="text-sm text-gray-500">Obligatoire</p>
          </div>
        </div>

        {formData.propertyConstructionPeriod === 'avant 1949' && (
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                name="annexLeadRisk"
                checked={formData.annexLeadRisk}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </div>
            <div className="ml-3">
              <label className="font-medium text-gray-700">
                Constat de risque d'exposition au plomb (CREP) *
              </label>
              <p className="text-sm text-gray-500">Obligatoire pour les immeubles construits avant le 1er janvier 1949</p>
            </div>
          </div>
        )}

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexAsbestos"
              checked={formData.annexAsbestos}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              État mentionnant l'absence ou la présence d'amiante *
            </label>
            <p className="text-sm text-gray-500">Obligatoire</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexElectricGas"
              checked={formData.annexElectricGas}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              État de l'installation intérieure d'électricité et de gaz *
            </label>
            <p className="text-sm text-gray-500">Obligatoire si installation de plus de 15 ans</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexNaturalRisks"
              checked={formData.annexNaturalRisks}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              État des risques naturels et technologiques
            </label>
            <p className="text-sm text-gray-500">Si applicable selon la zone</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexNotice"
              checked={formData.annexNotice}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              Notice d'information *
            </label>
            <p className="text-sm text-gray-500">Droits et obligations des locataires et bailleurs - Obligatoire</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="annexInventory"
              checked={formData.annexInventory}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
          </div>
          <div className="ml-3">
            <label className="font-medium text-gray-700">
              État des lieux *
            </label>
            <p className="text-sm text-gray-500">Obligatoire</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Lieu de signature
        </label>
        <input
          type="text"
          name="signaturePlace"
          value={formData.signaturePlace}
          onChange={handleChange}
          placeholder="Ex: Paris"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Modifier le contrat' : 'Nouveau contrat de location'}
        </h1>
        <p className="text-gray-600 mt-2">
          Contrat type de location de logement nu (non meublé) - Résidence principale
        </p>
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            ℹ️ Ce contrat est conforme à la loi du 6 juillet 1989 et s'applique uniquement aux
            locations de logement nu (non meublé) en France.
          </p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="space-y-4 md:flex md:space-y-0 md:space-x-8">
            {steps.map((step, index) => (
              <li key={step.title} className="md:flex-1">
                <div
                  className={`group pl-4 py-2 flex flex-col border-l-4 md:pl-0 md:pt-4 md:pb-0 md:border-l-0 md:border-t-4 ${
                    index < currentStep
                      ? 'border-primary-600'
                      : index === currentStep
                      ? 'border-primary-600'
                      : 'border-gray-200'
                  }`}
                >
                  <span
                    className={`text-xs font-semibold tracking-wide uppercase ${
                      index <= currentStep ? 'text-primary-600' : 'text-gray-500'
                    }`}
                  >
                    Étape {index + 1}
                  </span>
                  <span className="text-sm font-medium">{step.title}</span>
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {steps[currentStep].title}
          </h2>
          <p className="text-gray-600 text-sm">{steps[currentStep].description}</p>
        </div>

        {renderStepContent()}

        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ChevronLeftIcon className="h-5 w-5 mr-1" />
            Précédent
          </button>

          <div className="flex space-x-3">
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Suivant
                <ChevronRightIcon className="h-5 w-5 ml-1" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
              >
                {loading ? (
                  'Enregistrement...'
                ) : (
                  <>
                    <CheckIcon className="h-5 w-5 mr-1" />
                    {id ? 'Mettre à jour' : 'Créer le contrat'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
